// src/render/particles.ts -- render layer, uses Math.random() (cosmetic only)

export interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  r: number;
  g: number;
  b: number;
  size: number;
  gravityScale: number;  // 0 = no gravity (trails), 0.3 = normal
}

const POOL_SIZE = 768;
const pool: Particle[] = [];

// Pre-allocate at module load
for (let i = 0; i < POOL_SIZE; i++) {
  pool.push({
    active: false,
    x: 0, y: 0, vx: 0, vy: 0,
    life: 0, maxLife: 1,
    r: 255, g: 255, b: 255,
    size: 1,
    gravityScale: 0.3,
  });
}

// Flash overlay alpha (white screen flash on explosion)
let flashAlpha = 0;

function spawnParticle(
  x: number, y: number,
  vx: number, vy: number,
  life: number,
  r: number, g: number, b: number,
  size: number,
  gravityScale = 0.3,
): void {
  for (const p of pool) {
    if (!p.active) {
      p.active = true;
      p.x = x;
      p.y = y;
      p.vx = vx;
      p.vy = vy;
      p.life = life;
      p.maxLife = life;
      p.r = r;
      p.g = g;
      p.b = b;
      p.size = size;
      p.gravityScale = gravityScale;
      return;
    }
  }
  // Pool exhausted -- silently drop. No allocation.
}

export interface ExplosionPalette {
  fire: [number, number, number];
  spark: [number, number, number];
  ember: [number, number, number];
}

// Named palettes for weapon types
export const PALETTE_DEFAULT: ExplosionPalette = { fire: [255, 160, 40], spark: [255, 220, 80], ember: [255, 80, 20] };
export const PALETTE_NUKE: ExplosionPalette = { fire: [255, 255, 240], spark: [255, 255, 200], ember: [180, 200, 255] };
export const PALETTE_DIRT: ExplosionPalette = { fire: [160, 120, 60], spark: [200, 160, 80], ember: [100, 70, 30] };
export const PALETTE_NAPALM: ExplosionPalette = { fire: [255, 100, 20], spark: [255, 60, 10], ember: [200, 40, 5] };
export const PALETTE_ROLLER: ExplosionPalette = { fire: [200, 200, 200], spark: [255, 220, 100], ember: [150, 150, 150] };
export const PALETTE_FUNKY: ExplosionPalette = { fire: [255, 50, 255], spark: [50, 255, 50], ember: [255, 255, 50] };
export const PALETTE_RIOT: ExplosionPalette = { fire: [255, 80, 80], spark: [255, 180, 100], ember: [200, 50, 30] };
export const PALETTE_ENERGY: ExplosionPalette = { fire: [100, 255, 255], spark: [200, 255, 255], ember: [50, 150, 255] };
export const PALETTE_DIGGER: ExplosionPalette = { fire: [180, 140, 80], spark: [220, 180, 100], ember: [120, 90, 50] };

export function getWeaponPalette(weaponId?: string): ExplosionPalette {
  if (!weaponId) return PALETTE_DEFAULT;
  if (weaponId.includes('nuke') || weaponId === 'deaths-head') return PALETTE_NUKE;
  if (weaponId.includes('dirt')) return PALETTE_DIRT;
  if (weaponId.includes('napalm')) return PALETTE_NAPALM;
  if (weaponId.includes('roller')) return PALETTE_ROLLER;
  if (weaponId.includes('funky') || weaponId.includes('mirv')) return PALETTE_FUNKY;
  if (weaponId.includes('riot')) return PALETTE_RIOT;
  if (weaponId === 'laser' || weaponId === 'plasma-blast') return PALETTE_ENERGY;
  if (weaponId.includes('digger') || weaponId.includes('sandhog')) return PALETTE_DIGGER;
  return PALETTE_DEFAULT;
}

/** Spawn a multi-layer explosion at (x, y) scaled by blast radius. */
export function spawnExplosion(x: number, y: number, radius: number, palette?: ExplosionPalette, weaponId?: string): void {
  const p = palette ?? PALETTE_DEFAULT;
  const scale = Math.max(0.5, radius / 14); // 14 = baseline blast radius

  // Nuke-class weapons get a special mushroom cloud effect
  const isNuke = weaponId !== undefined && (weaponId.includes('nuke') || weaponId === 'deaths-head' || weaponId === 'plasma-blast');
  const isDirt = weaponId !== undefined && weaponId.includes('dirt');
  const isNapalm = weaponId !== undefined && weaponId.includes('napalm');
  const isFunky = weaponId !== undefined && (weaponId.includes('funky') || weaponId.includes('mirv'));
  const isRiot = weaponId !== undefined && weaponId.includes('riot');
  const isEnergy = weaponId === 'laser' || weaponId === 'plasma-blast';

  // Screen flash scales with blast size
  flashAlpha = isNuke ? Math.min(1.0, 0.4 + scale * 0.1) : Math.min(0.8, 0.3 + scale * 0.05);

  if (isNuke) {
    spawnNukeExplosion(x, y, scale, p, weaponId);
    return;
  }
  if (isDirt) {
    spawnDirtExplosion(x, y, scale, p);
    return;
  }
  if (isNapalm) {
    spawnNapalmExplosion(x, y, scale, p);
    return;
  }
  if (isFunky) {
    spawnFunkyExplosion(x, y, scale, p);
    return;
  }
  if (isRiot) {
    spawnRiotExplosion(x, y, scale, p);
    return;
  }
  if (isEnergy) {
    spawnEnergyExplosion(x, y, scale, p);
    return;
  }

  // --- Standard explosion (missiles, rollers, leapfrogs, etc.) ---
  // Fireball core
  const fireballCount = Math.round((15 + Math.random() * 10) * scale);
  for (let i = 0; i < fireballCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = (20 + Math.random() * 40) * Math.min(scale, 3);
    spawnParticle(
      x, y,
      Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.25 + Math.random() * 0.15,
      p.fire[0], p.fire[1], p.fire[2],
      2 + Math.round(Math.random() * scale),
    );
  }

  // Sparks fly outward fast
  const sparkCount = Math.round((20 + Math.random() * 15) * scale);
  for (let i = 0; i < sparkCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = (60 + Math.random() * 100) * Math.min(scale, 2.5);
    spawnParticle(
      x, y,
      Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.3 + Math.random() * 0.3,
      p.spark[0], p.spark[1], p.spark[2],
      1,
    );
  }

  // Embers (slower, longer-lived)
  const emberCount = Math.round((8 + Math.random() * 8) * scale);
  for (let i = 0; i < emberCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 30 + Math.random() * 50;
    spawnParticle(
      x, y,
      Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.5 + Math.random() * 0.3,
      p.ember[0], p.ember[1], p.ember[2],
      1,
    );
  }

  // Smoke plume (rises upward)
  const smokeCount = Math.round((8 + Math.random() * 4) * scale);
  for (let i = 0; i < smokeCount; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.0;
    const speed = 15 + Math.random() * 25;
    spawnParticle(
      x, y,
      Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.6 + Math.random() * 0.4,
      80, 80, 80,
      2 + Math.round(Math.random()),
      0.05, // smoke barely affected by gravity
    );
  }
}

/** Nuke: massive mushroom cloud with bright white core and rising column */
function spawnNukeExplosion(x: number, y: number, scale: number, p: ExplosionPalette, weaponId?: string): void {
  // Tier-specific tuning: Death's Head gets red-orange hellfire, Nuke gets classic mushroom, Baby Nuke gets smaller bright flash
  const isDeathsHead = weaponId === 'deaths-head';
  const isBabyNuke = weaponId === 'baby-nuke';

  // Death's Head override palette: menacing red/orange
  const firePalette: [number, number, number] = isDeathsHead ? [255, 80, 20] : p.fire;
  const sparkPalette: [number, number, number] = isDeathsHead ? [255, 200, 80] : p.spark;
  const emberPalette: [number, number, number] = isDeathsHead ? [255, 40, 10] : p.ember;
  const smokeColor: [number, number, number] = isDeathsHead ? [60, 40, 40] : [100, 100, 100];

  const intensity = isDeathsHead ? 1.4 : isBabyNuke ? 0.7 : 1.0;

  // Bright core flash
  const coreCount = Math.round(25 * scale * intensity);
  for (let i = 0; i < coreCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 10 + Math.random() * 30;
    const coreColor: [number, number, number] = isDeathsHead ? [255, 220, 150] : [255, 255, 255];
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.4 + Math.random() * 0.2, coreColor[0], coreColor[1], coreColor[2],
      3 + Math.round(Math.random() * 2));
  }

  // Fire ring expanding outward
  const ringCount = Math.round(40 * scale * intensity);
  for (let i = 0; i < ringCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = (50 + Math.random() * 80) * Math.min(scale, 4);
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.4 + Math.random() * 0.3, firePalette[0], firePalette[1], firePalette[2],
      2 + Math.round(Math.random() * 2));
  }

  // Mushroom cap
  const capCount = Math.round(30 * scale * intensity);
  for (let i = 0; i < capCount; i++) {
    const spread = (Math.random() - 0.5) * 60 * scale;
    const rise = -(120 + Math.random() * 80) * Math.min(scale, 3);
    spawnParticle(x + spread * 0.3, y, spread * 0.8, rise,
      0.6 + Math.random() * 0.5, sparkPalette[0], sparkPalette[1], sparkPalette[2],
      2 + Math.round(Math.random() * 2), 0.1);
  }

  // Rising column (stem) — more dramatic for Death's Head
  const stemCount = Math.round(20 * scale * intensity);
  for (let i = 0; i < stemCount; i++) {
    const spread = (Math.random() - 0.5) * 10;
    const rise = -(80 + Math.random() * 120) * (isDeathsHead ? 1.3 : 1.0);
    spawnParticle(x, y, spread, rise,
      0.5 + Math.random() * 0.4, emberPalette[0], emberPalette[1], emberPalette[2],
      2, 0.05);
  }

  // Smoke cloud
  const smokeCount = Math.round(25 * scale * intensity);
  for (let i = 0; i < smokeCount; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.5;
    const speed = 20 + Math.random() * 50;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      1.0 + Math.random() * 0.8, smokeColor[0], smokeColor[1], smokeColor[2],
      3 + Math.round(Math.random() * 2), 0.02);
  }

  // Ground debris
  const debrisCount = Math.round(15 * scale * intensity);
  for (let i = 0; i < debrisCount; i++) {
    const angle = -Math.PI * 0.1 - Math.random() * Math.PI * 0.8;
    const speed = 100 + Math.random() * 150;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.5 + Math.random() * 0.4, 160, 120, 60, 1);
  }

  // Death's Head: extra red shockwave ring
  if (isDeathsHead) {
    const shockCount = Math.round(30 * scale);
    for (let i = 0; i < shockCount; i++) {
      const angle = (i / shockCount) * Math.PI * 2;
      const speed = 180 + Math.random() * 60;
      spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
        0.3, 255, 50, 20, 2, 0.0);
    }
  }
}

/** Dirt: brown spray upward, no fire */
function spawnDirtExplosion(x: number, y: number, scale: number, p: ExplosionPalette): void {
  // Dirt chunks spray upward
  const chunkCount = Math.round(30 * scale);
  for (let i = 0; i < chunkCount; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4;
    const speed = 40 + Math.random() * 80;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.5 + Math.random() * 0.5,
      p.fire[0] + Math.round((Math.random() - 0.5) * 30),
      p.fire[1] + Math.round((Math.random() - 0.5) * 30),
      p.fire[2] + Math.round((Math.random() - 0.5) * 20),
      2 + Math.round(Math.random()));
  }

  // Dust cloud
  const dustCount = Math.round(15 * scale);
  for (let i = 0; i < dustCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 10 + Math.random() * 30;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.4 + Math.random() * 0.3, 140, 120, 90, 2, 0.05);
  }
}

/** Napalm: dripping fire, orange-red flames spreading */
function spawnNapalmExplosion(x: number, y: number, scale: number, p: ExplosionPalette): void {
  // Flames spreading sideways
  const flameCount = Math.round(20 * scale);
  for (let i = 0; i < flameCount; i++) {
    const dir = Math.random() > 0.5 ? 1 : -1;
    const vx = dir * (30 + Math.random() * 60);
    const vy = -(10 + Math.random() * 40);
    spawnParticle(x, y, vx, vy,
      0.4 + Math.random() * 0.4, p.fire[0], p.fire[1], p.fire[2],
      2 + Math.round(Math.random()), 0.15);
  }

  // Dripping fire particles falling
  const dripCount = Math.round(12 * scale);
  for (let i = 0; i < dripCount; i++) {
    const vx = (Math.random() - 0.5) * 40;
    const vy = 10 + Math.random() * 30;
    spawnParticle(x + (Math.random() - 0.5) * 20, y, vx, vy,
      0.6 + Math.random() * 0.4, p.spark[0], p.spark[1], p.spark[2],
      1, 0.5);
  }

  // Black smoke
  const smokeCount = Math.round(10 * scale);
  for (let i = 0; i < smokeCount; i++) {
    spawnParticle(x + (Math.random() - 0.5) * 20, y,
      (Math.random() - 0.5) * 15, -(20 + Math.random() * 30),
      0.8 + Math.random() * 0.5, 40, 40, 40, 3, 0.02);
  }
}

/** Funky bomb: rainbow scatter in all directions */
function spawnFunkyExplosion(x: number, y: number, scale: number, _p: ExplosionPalette): void {
  const count = Math.round(40 * scale);
  for (let i = 0; i < count; i++) {
    const hue = (i / count) * 360;
    const [r, g, b] = hslToRgb(hue, 1.0, 0.6);
    const angle = Math.random() * Math.PI * 2;
    const speed = 40 + Math.random() * 100;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.4 + Math.random() * 0.4, r, g, b,
      1 + Math.round(Math.random()));
  }

  // Center flash
  for (let i = 0; i < 8; i++) {
    spawnParticle(x, y, (Math.random() - 0.5) * 20, (Math.random() - 0.5) * 20,
      0.2, 255, 255, 255, 3);
  }
}

/** Riot: directional blast spray */
function spawnRiotExplosion(x: number, y: number, scale: number, p: ExplosionPalette): void {
  const count = Math.round(25 * scale);
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 60 + Math.random() * 80;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.3 + Math.random() * 0.3, p.fire[0], p.fire[1], p.fire[2],
      1 + Math.round(Math.random()));
  }

  // Shockwave ring
  const ringCount = Math.round(16 * scale);
  for (let i = 0; i < ringCount; i++) {
    const angle = (i / ringCount) * Math.PI * 2;
    const speed = 80 + Math.random() * 40;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.2, 255, 200, 150, 1);
  }
}

/** Energy: cyan/blue electric burst */
function spawnEnergyExplosion(x: number, y: number, scale: number, p: ExplosionPalette): void {
  // Electric arcs (straight lines outward)
  const arcCount = Math.round(20 * scale);
  for (let i = 0; i < arcCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 80 + Math.random() * 120;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.15 + Math.random() * 0.15, p.spark[0], p.spark[1], p.spark[2],
      1, 0.0); // no gravity for energy
  }

  // Glow core
  const glowCount = Math.round(12 * scale);
  for (let i = 0; i < glowCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 10 + Math.random() * 25;
    spawnParticle(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed,
      0.3 + Math.random() * 0.2, p.fire[0], p.fire[1], p.fire[2],
      2 + Math.round(Math.random()), 0.0);
  }
}

/** Spawn trail particles behind a flying projectile. Called each frame from draw. */
export function spawnTrail(x: number, y: number, vx: number, vy: number, weaponId: string): void {
  const cat = getCategoryForTrail(weaponId);

  if (cat === 'tracer') {
    // Smoke tracers leave a visible dotted trail
    if (weaponId === 'smoke-tracer') {
      spawnParticle(x, y, (Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5,
        1.5, 200, 200, 200, 2, 0.0);
    }
    return; // regular tracer: no trail
  }

  if (cat === 'nuke') {
    // Nukes glow and leave a hot trail
    spawnParticle(x, y, (Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8,
      0.2 + Math.random() * 0.15, 255, 255, 200, 2, 0.0);
    if (Math.random() < 0.3) {
      spawnParticle(x, y, -vx * 0.05 + (Math.random() - 0.5) * 10, -vy * 0.05,
        0.3, 255, 200, 100, 1, 0.05);
    }
    return;
  }

  if (cat === 'napalm') {
    // Fire trail
    spawnParticle(x, y,
      -vx * 0.03 + (Math.random() - 0.5) * 8,
      -vy * 0.03 + (Math.random() - 0.5) * 8,
      0.2 + Math.random() * 0.15, 255, 100 + Math.round(Math.random() * 60), 20,
      1 + Math.round(Math.random()), 0.1);
    return;
  }

  if (cat === 'energy') {
    // Electric crackle
    spawnParticle(x + (Math.random() - 0.5) * 6, y + (Math.random() - 0.5) * 6,
      (Math.random() - 0.5) * 20, (Math.random() - 0.5) * 20,
      0.1 + Math.random() * 0.1, 100, 255, 255, 1, 0.0);
    return;
  }

  if (cat === 'dirt') {
    // Brown dust
    if (Math.random() < 0.4) {
      spawnParticle(x, y, (Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5,
        0.15, 160, 120, 60, 1, 0.1);
    }
    return;
  }

  // Default: small smoke puff every few frames
  if (Math.random() < 0.5) {
    spawnParticle(x, y,
      -vx * 0.02 + (Math.random() - 0.5) * 4,
      -vy * 0.02 + (Math.random() - 0.5) * 4,
      0.15 + Math.random() * 0.1, 180, 160, 140, 1, 0.05);
  }
}

function getCategoryForTrail(weaponId: string): string {
  if (weaponId.includes('nuke') || weaponId === 'deaths-head') return 'nuke';
  if (weaponId.includes('napalm')) return 'napalm';
  if (weaponId === 'laser' || weaponId === 'plasma-blast') return 'energy';
  if (weaponId.includes('tracer') || weaponId === 'smoke-tracer') return 'tracer';
  if (weaponId.includes('dirt')) return 'dirt';
  return 'standard';
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

/** Update all active particles. Call once per frame. */
export function updateParticles(dt: number, gravity: number): void {
  for (let i = 0; i < POOL_SIZE; i++) {
    const p = pool[i];
    if (!p.active) continue;
    p.vy += gravity * p.gravityScale * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.life -= dt;
    if (p.life <= 0) p.active = false;
  }
}

/** Draw all active particles with additive blending. */
export function drawParticles(ctx: CanvasRenderingContext2D, dt: number): void {
  // Flash overlay
  if (flashAlpha > 0) {
    ctx.save();
    ctx.globalAlpha = flashAlpha;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
    flashAlpha -= dt * 10;
    if (flashAlpha < 0) flashAlpha = 0;
  }

  ctx.save();
  ctx.globalCompositeOperation = 'lighter'; // additive blending
  for (let i = 0; i < POOL_SIZE; i++) {
    const p = pool[i];
    if (!p.active) continue;
    const alpha = Math.max(0, p.life / p.maxLife);
    ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${alpha.toFixed(2)})`;
    // Larger particles get a soft glow halo
    if (p.size >= 3) {
      ctx.globalAlpha = alpha * 0.3;
      ctx.fillRect((p.x | 0) - 1, (p.y | 0) - 1, p.size + 2, p.size + 2);
      ctx.globalAlpha = 1;
    }
    ctx.fillRect(p.x | 0, p.y | 0, p.size, p.size);
  }
  ctx.restore();
}
